import React from 'react';
import { useCustomActions } from '../hooks';
import { Icon } from '@/shared/icons'
interface CustomActionsListProps {
  selectedActionIds: string[];
  onRemove: (actionId: string) => void;
  onViewDetails?: (actionId: string) => void;
}

/**
 * Displays a list of selected custom actions with ability to remove them
 * Used in AI Agent form to show which custom actions are assigned to the agent
 */
export const CustomActionsList: React.FC<CustomActionsListProps> = ({
  selectedActionIds,
  onRemove,
  onViewDetails,
}) => {
  const { data: actionsData, isLoading } = useCustomActions({ status: 'active' });

  // Filter only selected actions
  const selectedActions = React.useMemo(() => {
    if (!actionsData?.data) return [];
    return actionsData.data.filter((action: import('@troia-v3/types').CustomActionResponse) => selectedActionIds.includes(action.id));
  }, [actionsData, selectedActionIds]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (selectedActions.length === 0) {
    return (
      <div
        className="text-center py-8 rounded-lg border-2 border-dashed"
        style={{
          borderColor: 'var(--theme-card-border)',
          color: 'var(--theme-muted-text)',
        }}
      >
        <Icon name="code" className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Nenhuma ferramenta selecionada</p>
        <p className="text-xs mt-1">Use o seletor abaixo para adicionar ferramentas ao agente</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {selectedActions.map((action) => (
        <div
          key={action.id}
          className="p-4 rounded-lg border transition-colors"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          <div className="flex items-start justify-between gap-4">
            {/* Action Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h4
                  className="text-sm font-semibold truncate"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {action.displayName}
                </h4>
                <span
                  className="px-2 py-0.5 text-xs rounded-full"
                  style={{
                    backgroundColor: 'var(--theme-success-background)',
                    color: 'var(--theme-success-color)',
                  }}
                >
                  {action.endpoint.method}
                </span>
              </div>

              {action.description && (
                <p
                  className="text-xs mb-2 line-clamp-2"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  {action.description}
                </p>
              )}

              {/* Endpoint URL */}
              <div className="flex items-center gap-2">
                <code
                  className="text-xs px-2 py-1 rounded"
                  style={{
                    backgroundColor: 'var(--theme-input-background)',
                    color: 'var(--theme-muted-text)',
                  }}
                >
                  {action.endpoint.url}
                </code>
              </div>

              {/* Parameters Count */}
              {action.parameters?.properties && Object.keys(action.parameters.properties).length > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className="text-xs"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    {Object.keys(action.parameters.properties).length} parâmetro{Object.keys(action.parameters.properties).length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {onViewDetails && (
                <button
                  onClick={() => onViewDetails(action.id)}
                  className="p-2 rounded-lg transition-colors"
                  style={{ color: 'var(--theme-muted-text)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                    e.currentTarget.style.color = 'var(--theme-card-text)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--theme-muted-text)';
                  }}
                  title="Ver detalhes"
                >
                  <Icon name="external-link" className="w-4 h-4" />
                </button>
              )}

              <button
                onClick={() => onRemove(action.id)}
                className="p-2 rounded-lg transition-colors"
                style={{ color: 'var(--theme-error-color)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--theme-error-background)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title="Remover action"
              >
                <Icon name="trash" className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
